import { RegionShared } from "@1games-ru/region-shared";
import {
  GetEstateSafesRequest,
  GetEstateSafesResponse,
} from "@1games-ru/region-shared/dist/backend/requests/estate";
import {
  EquipmentSlot,
  EquipmentSlotType,
  InventoryItem,
  QuickSlot,
} from "@1games-ru/region-shared/dist/types/inventory/inventory";
import { useCallback } from "react";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import RpcMpProvider from "@/core/rpc-mp-provider/rpc-mp-provider";
import { EquipSlot } from "@/domains/inventory/constants/equipSlot";
import { OwnerType } from "@/domains/inventory/constants/ownerType";
import {
  addEquipItem,
  addInventoryItem,
  addQuickSlotItem,
  changeInventoryItem,
  removeEquipItem,
  removeInventoryItem,
  removeQuickSlotItem,
  setSafe,
} from "@/domains/inventory/slice";
import toast from "@/domains/toasts/helpers/toast";

export default function useInventoryManager() {
  const dispatch = useAppDispatch();

  const addItem = useCallback(
    async (data: {
      details: string[];
      inventoryOwnerId: string;
      inventoryOwnerType: string;
      itemId: number;
      quantity: number;
    }) => {
      const response = await RpcMpProvider.getInstance().callServer(
        RegionShared.Backend.Events.Admin.Inventory.AddItem,
        data,
      );

      if (response instanceof RpcMpProviderFailedResponse) {
        toast.error(response.message);
        console.log({ addItemResponse: response, data });
      }
    },
    [],
  );

  const moveItem = useCallback(
    async (
      fromInventoryId: number,
      toInventoryId: number,
      item: InventoryItem,
      oldItem?: InventoryItem,
    ) => {
      if (fromInventoryId === toInventoryId) {
        dispatch(
          changeInventoryItem({
            inventoryId: toInventoryId,
            inventoryItem: item,
          }),
        );
      } else {
        dispatch(removeInventoryItem({ inventoryId: fromInventoryId, inventoryItemId: item.id }));
        dispatch(
          addInventoryItem({
            inventoryId: toInventoryId,
            inventoryItem: { ...item, inventoryId: toInventoryId },
          }),
        );
      }

      const response = await RpcMpProvider.getInstance().callServer<
        typeof RegionShared.Backend.Requests.InventoryMoveRequest,
        typeof RegionShared.Backend.Responses.InventoryMoveResponse
      >(RegionShared.Backend.Events.Inventory.MoveItem, {
        fromInventoryId: fromInventoryId,
        toInventoryId: toInventoryId,
        inventoryItemId: item.id,
        position: item.position,
        isRotated: item.isRotated,
      });

      if (response instanceof RpcMpProviderFailedResponse) {
        toast.error(response.message);
        console.log({ response });
        if (oldItem) {
          if (fromInventoryId === toInventoryId) {
            dispatch(
              changeInventoryItem({
                inventoryId: toInventoryId,
                inventoryItem: oldItem,
              }),
            );
          } else {
            dispatch(removeInventoryItem({ inventoryId: toInventoryId, inventoryItemId: item.id }));
            dispatch(addInventoryItem({ inventoryId: fromInventoryId, inventoryItem: oldItem }));
          }
        }
      }
    },
    [],
  );

  const pickItem = useCallback(async (fromInventoryId: number, item: InventoryItem) => {
    dispatch(removeInventoryItem({ inventoryId: fromInventoryId, inventoryItemId: item.id }));
    const response = await RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.PickupItemRequest,
      typeof RegionShared.Backend.Responses.PickupItemResponse
    >(RegionShared.Backend.Events.Inventory.PickupItem, {
      inventoryItemId: item.id,
    });

    if (response instanceof RpcMpProviderFailedResponse) {
      toast.error(response.message);
      console.log({ response });
      dispatch(addInventoryItem({ inventoryId: fromInventoryId, inventoryItem: item }));
    }
  }, []);

  const dropItem = useCallback(async (fromInventoryId: number, item: InventoryItem) => {
    dispatch(removeInventoryItem({ inventoryId: fromInventoryId, inventoryItemId: item.id }));

    const response = await RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.InventoryDropItemRequest,
      typeof RegionShared.Backend.Responses.InventoryDropItemResponse
    >(RegionShared.Backend.Events.Inventory.DropItem, {
      fromInventoryId: fromInventoryId,
      inventoryItemId: item.id,
    });

    if (response instanceof RpcMpProviderFailedResponse) {
      toast.error(response.message);
      console.log(response);
      dispatch(addInventoryItem({ inventoryId: fromInventoryId, inventoryItem: item }));
    }
  }, []);

  const applyItem = useCallback(async (fromInventoryId: number, item: InventoryItem) => {
    if (item.quantity === 1) {
      dispatch(
        removeInventoryItem({
          inventoryId: fromInventoryId,
          inventoryItemId: item.id,
        }),
      );
    } else {
      dispatch(
        changeInventoryItem({
          inventoryId: fromInventoryId,
          inventoryItem: { ...item, quantity: item.quantity - 1 },
        }),
      );
    }

    const response = await RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.InventoryUseItemRequest,
      typeof RegionShared.Backend.Responses.InventoryUseItemResponse
    >(RegionShared.Backend.Events.Inventory.UseItem, {
      fromInventoryId: fromInventoryId,
      inventoryItemId: item.id,
    });

    if (response instanceof RpcMpProviderFailedResponse) {
      toast.error(response.message);
      console.log({ response });
      if (item.quantity === 1) {
        dispatch(addInventoryItem({ inventoryId: fromInventoryId, inventoryItem: item }));
      } else {
        dispatch(changeInventoryItem({ inventoryId: fromInventoryId, inventoryItem: item }));
      }
    }
  }, []);

  const equipItem = useCallback(async (equipmentId: number, slotItem: EquipmentSlot) => {
    dispatch(
      addEquipItem({
        equipmentId,
        slotItem,
      }),
    );
    dispatch(
      removeInventoryItem({
        inventoryId: slotItem.item.inventoryId,
        inventoryItemId: slotItem.item.id,
      }),
    );

    const response = await RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.InventoryEquipRequest,
      typeof RegionShared.Backend.Responses.InventoryEquipResponse
    >(RegionShared.Backend.Events.Inventory.EquipItem, {
      fromInventoryId: slotItem.item.inventoryId,
      inventoryItemId: slotItem.item.id,
    });

    if (response instanceof RpcMpProviderFailedResponse) {
      toast.error(response.message);
      console.log({ response });
      dispatch(removeEquipItem({ equipmentId, slot: slotItem.slot }));
      dispatch(
        addInventoryItem({
          inventoryId: slotItem.item.inventoryId,
          inventoryItem: slotItem.item,
        }),
      );
    }
  }, []);

  const unEquipItem = useCallback(
    async (equipmentId: number, slotType: string, item: InventoryItem) => {
      dispatch(removeEquipItem({ equipmentId, slot: slotType }));
      if (item?.position) {
        dispatch(addInventoryItem({ inventoryId: item.inventoryId, inventoryItem: item }));
      }

      const response = await RpcMpProvider.getInstance().callServer<
        typeof RegionShared.Backend.Requests.InventoryUnEquipRequest,
        typeof RegionShared.Backend.Responses.InventoryUnEquipResponse
      >(RegionShared.Backend.Events.Inventory.UnEquipItem, {
        slotType: slotType as EquipmentSlotType,
        position: item?.position,
      });

      if (response instanceof RpcMpProviderFailedResponse) {
        toast.error(response.message);
        console.log({ response });
        if (item?.position) {
          dispatch(
            removeInventoryItem({ inventoryId: item.inventoryId, inventoryItemId: item.id }),
          );
        }
        dispatch(
          addEquipItem({
            equipmentId,
            slotItem: {
              slot: slotType,
              item,
            },
          }),
        );
      }
    },
    [],
  );

  const stackItems = useCallback(
    async (
      fromInventoryId: number,
      toInventoryId: number,
      fromItem: InventoryItem,
      toItem: InventoryItem,
    ) => {
      if (fromItem.itemId === toItem.itemId && fromItem.id === toItem.id) {
        const toEmptyQuantity = toItem.itemTemplate.maxStack - toItem.quantity;
        const quantityForTrasfer =
          toEmptyQuantity > fromItem.quantity ? fromItem.quantity : toEmptyQuantity; // если свободно больше, чем хотим перекинуть, то перекидываем все что можно, иначе заполняем до скольки можем

        dispatch(
          changeInventoryItem({
            inventoryId: toInventoryId,
            inventoryItem: {
              ...toItem,

              quantity: toItem.quantity + quantityForTrasfer,
            },
          }),
        );
        if (fromItem.quantity - quantityForTrasfer === 0) {
          dispatch(
            removeInventoryItem({ inventoryId: fromInventoryId, inventoryItemId: fromItem.id }),
          );
        } else {
          dispatch(
            changeInventoryItem({
              inventoryId: fromInventoryId,
              inventoryItem: {
                ...fromItem,
                quantity: fromItem.quantity - quantityForTrasfer,
              },
            }),
          );
        }

        const response = await RpcMpProvider.getInstance().callServer<
          typeof RegionShared.Backend.Requests.InventoryStackItemRequest,
          typeof RegionShared.Backend.Responses.InventoryStackItemResponse
        >(RegionShared.Backend.Events.Inventory.StackItem, {
          fromInventoryId: fromInventoryId,
          toInventoryId: toInventoryId,
          fromInventoryItemId: fromItem.id,
          toInventoryItemId: toItem.id,
        });

        if (response instanceof RpcMpProviderFailedResponse) {
          toast.error(response.message);
          console.log({ response });
          dispatch(
            changeInventoryItem({
              inventoryId: toInventoryId,
              inventoryItem: toItem,
            }),
          );
          if (fromItem.quantity - quantityForTrasfer === 0) {
            dispatch(addInventoryItem({ inventoryId: fromInventoryId, inventoryItem: fromItem }));
          } else {
            dispatch(
              changeInventoryItem({
                inventoryId: fromInventoryId,
                inventoryItem: fromItem,
              }),
            );
          }
        }
      }
    },
    [],
  );

  const attachItem = useCallback(
    async (
      fromInventoryId: number,
      toInventoryId: number,
      fromItem: InventoryItem,
      toItem: InventoryItem,
    ) => {
      const response = await RpcMpProvider.getInstance().callServer<
        typeof RegionShared.Backend.Requests.InventoryAttachItemRequest,
        typeof RegionShared.Backend.Responses.InventoryAttachItemResponse
      >(RegionShared.Backend.Events.Inventory.AttachItem, {
        fromInventoryId: fromInventoryId,
        toInventoryId: toInventoryId,
        inventoryItemId: fromItem.id,
        toInventoryItemId: toItem.id,
      });

      if (response instanceof RpcMpProviderFailedResponse) {
        toast.error(response.message);
        console.log({
          response,
        });
      }
    },
    [],
  );

  const detachItem = useCallback(
    async (
      fromInventoryId: number,
      toInventoryId: number,
      fromItem: InventoryItem,
      toItem: InventoryItem,
    ) => {
      const response = await RpcMpProvider.getInstance().callServer<
        typeof RegionShared.Backend.Requests.InventoryDetachItemRequest,
        typeof RegionShared.Backend.Responses.InventoryDetachItemResponse
      >(RegionShared.Backend.Events.Inventory.DetachItem, {
        fromInventoryId: fromInventoryId,
        toInventoryId: toInventoryId,
        fromInventoryItemId: fromItem.id,
        inventoryItemId: toItem.id,
      });

      if (response instanceof RpcMpProviderFailedResponse) {
        toast.error(response.message);
        console.log({ response });
      }
    },
    [],
  );

  const assignQuickSlotItem = useCallback(
    async (
      item: QuickSlot,
      from?: {
        slot?: EquipSlot;
        quickSlot?: number;
      },
    ) => {
      if (item.inventoryItem.inventory.ownerType === OwnerType.Bag) return;
      if (from?.quickSlot) {
        dispatch(removeQuickSlotItem({ slot: from.quickSlot }));
      }
      dispatch(addQuickSlotItem({ slotItem: item }));

      const response = await RpcMpProvider.getInstance().callServer<
        typeof RegionShared.Backend.Requests.InventoryQuickSlotAssignRequest,
        typeof RegionShared.Backend.Responses.InventoryQuickSlotAssignResponse
      >(RegionShared.Backend.Events.Inventory.AssignQuickSlot, {
        inventoryItemId: item.inventoryItem.id,
        slotNumber: item.slotNumber,
      });

      if (response instanceof RpcMpProviderFailedResponse) {
        toast.error(response.message);
        console.log({ response });
        dispatch(removeQuickSlotItem({ slot: item.slotNumber }));
        if (from?.quickSlot) {
          dispatch(addQuickSlotItem({ slotItem: { ...item, slotNumber: from.quickSlot } }));
        }
      }
    },
    [],
  );

  const unassignQuickSlotItem = useCallback(async (item: QuickSlot) => {
    dispatch(removeQuickSlotItem({ slot: item.slotNumber }));

    const response = await RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.InventoryQuickSlotUnassignRequest,
      typeof RegionShared.Backend.Responses.InventoryQuickSlotUnassignResponse
    >(RegionShared.Backend.Events.Inventory.UnassignQuickSlot, { slotNumber: item.slotNumber });

    if (response instanceof RpcMpProviderFailedResponse) {
      toast.error(response.message);
      console.log({ response });
      dispatch(addQuickSlotItem({ slotItem: item }));
    }
  }, []);

  const splitItem = useCallback(
    async (fromInventoryId: number, inventoryItemId: string, quantity: number) => {
      const response = await RpcMpProvider.getInstance().callServer<
        typeof RegionShared.Backend.Requests.InventorySplitItemRequest,
        typeof RegionShared.Backend.Responses.InventorySplitItemResponse
      >(RegionShared.Backend.Events.Inventory.SplitItem, {
        fromInventoryId,
        inventoryItemId,
        quantity,
      });

      if (response instanceof RpcMpProviderFailedResponse) {
        toast.error(response.message);
        console.log({ response });
      }
    },
    [],
  );

  const getSafeState = useCallback(async (data: GetEstateSafesRequest) => {
    const response = await RpcMpProvider.callServer<GetEstateSafesRequest, GetEstateSafesResponse>(
      RegionShared.Backend.Events.Estate.GetEstateSafes,
      data,
    );

    if (response instanceof RpcMpProviderSuccessResponse) {
      const safe = response.data;
      dispatch(setSafe({ ...safe, estateId: data.estateId }));
    }
    if (response instanceof RpcMpProviderFailedResponse) {
      console.log({ response });
      toast.error(response.message);
    }
  }, []);

  return {
    moveItem,
    dropItem,
    applyItem,
    detachItem,
    attachItem,
    equipItem,
    unEquipItem,
    stackItems,
    assignQuickSlotItem,
    unassignQuickSlotItem,
    addItem,
    splitItem,
    pickItem,
    getSafeState,
  };
}
